import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Space, Breadcrumb, Card, Row, Col, Statistic, List, Tag, Empty, Spin, Typography } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  CloseCircleOutlined,
  WifiOutlined,
  MobileOutlined,
  AlertOutlined,
  QuestionCircleOutlined,
  EnvironmentOutlined
} from "@ant-design/icons";

import { useTitle } from "../helpers";
import MqttDeviceStore, { DashboardStats, DeviceFailure } from "../../stores/MqttDeviceStore";



function TotalDevicesActive({ stats }: { stats: DashboardStats }) {
  return (
    <Card className="dashboard-card">
      <Statistic
        title="Devices Online - Success"
        value={stats.totalActive}
        prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
        suffix="devices"
        valueStyle={{ color: '#52c41a' }}
      />
      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        Active devices with valid data in the last 24 hours
      </div>
    </Card>
  );
}

function TotalDevicesDeployed({ stats }: { stats: DashboardStats }) {
  return (
    <Card className="dashboard-card">
      <Statistic
        title="Total Devices Deployed"
        value={stats.totalDeployed}
        prefix={<MobileOutlined style={{ color: '#1890ff' }} />}
        suffix="devices"
        valueStyle={{ color: '#1890ff' }}
      />
      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        Success rate: {stats.totalDeployed > 0 ? Math.round((stats.totalActive / stats.totalDeployed) * 100) : 0}%
      </div>
    </Card>
  );
}

function DeviceFailureRate({ stats }: { stats: DashboardStats }) {
  return (
    <Card className="dashboard-card">
      <Statistic
        title="Device Issues Rate"
        value={stats.failureRate}
        prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
        suffix="%"
        precision={1}
        valueStyle={{ color: '#faad14' }}
      />
      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        {stats.totalDeployed - stats.totalActive} devices with issues
      </div>
    </Card>
  );
}

function DevicesOnlineInvalid({ stats }: { stats: DashboardStats }) {
  return (
    <Card className="dashboard-card">
      <Statistic
        title="Devices Online - Invalid"
        value={stats.totalOnlineInvalid}
        prefix={<QuestionCircleOutlined style={{ color: '#fa8c16' }} />}
        suffix="devices"
        valueStyle={{ color: '#fa8c16' }}
      />
      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        Active devices with invalid data in most recent packet
      </div>
    </Card>
  );
}

function DevicesOffline({ stats }: { stats: DashboardStats }) {
  return (
    <Card className="dashboard-card">
      <Statistic
        title="Devices Offline"
        value={stats.totalOffline}
        prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        suffix="devices"
        valueStyle={{ color: '#ff4d4f' }}
      />
      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        Devices with no packets received in the last 24 hours
      </div>
    </Card>
  );
}

function DevicesInactive({ stats }: { stats: DashboardStats }) {
  // Placeholder value for now
  const inactiveCount = stats.totalInactive;
  
  return (
    <Card className="dashboard-card">
      <Statistic
        title="Devices Inactive"
        value={inactiveCount}
        prefix={<AlertOutlined style={{ color: '#8c8c8c' }} />}
        suffix="devices"
        valueStyle={{ color: '#8c8c8c' }}
      />
      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        Devices currently not deployed, excluded from statistics
      </div>
    </Card>
  );
}

function DeviceFailureList({ failureList }: { failureList: DeviceFailure[] }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'offline':
        return 'red';
      case 'online-success':
        return 'green';
      case 'online-invalid':
        return 'orange';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'offline':
        return <CloseCircleOutlined />;
      case 'online-success':
        return <CheckCircleOutlined />;
      case 'online-invalid':
        return <QuestionCircleOutlined />;
      case 'inactive':
        return <AlertOutlined />;
      default:
        return <AlertOutlined />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'offline':
        return 'OFFLINE';
      case 'online-success':
        return 'ONLINE - SUCCESS';
      case 'online-invalid':
        return 'ONLINE - INVALID';
      case 'inactive':
        return 'INACTIVE';
      default:
        return status.toUpperCase();
    }
  };

  if (failureList.length === 0) {
    return (
      <Card 
        title={
          <span>
            <CheckCircleOutlined style={{ marginRight: 8 }} />
            Device Status List
          </span>
        } 
        className="dashboard-card"
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="All devices are online with valid data"
        />
      </Card>
    );
  }

  return (
    <Card 
      title={
        <span>
          <AlertOutlined style={{ marginRight: 8 }} />
          Device Status List
        </span>
      } 
      className="dashboard-card"
    >
      <List
        dataSource={failureList}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Tag color={getStatusColor(item.status)} icon={getStatusIcon(item.status)}>
                {getStatusLabel(item.status)}
              </Tag>
            ]}
          >
            <List.Item.Meta
              title={
                <Link to={`/mqtt-devices/${item.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  <span style={{ cursor: 'pointer' }}>
                    <WifiOutlined style={{ marginRight: 8 }} />
                    {item.name}
                  </span>
                </Link>
              }
              description={
                <div>
                  <div><strong>ID:</strong> {item.id}</div>
                  <div><strong>Location:</strong> {item.location}</div>
                  <div><strong>Last Seen:</strong> {item.lastSeen}</div>
                  <div><strong>Error:</strong> {item.error}</div>
                </div>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );
}

function DevStatDashboard() {
  useTitle("DevStat Dashboard", "Device Statistics");
  const [deviceStats, setDeviceStats] = useState<DashboardStats>({
    totalActive: 0,
    totalDeployed: 0,
    failureRate: 0,
    totalOffline: 0,
    totalInactive: 0,
    totalOnlineInvalid: 0,
    lastUpdated: new Date(),
    failureList: []
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await MqttDeviceStore.getDashboardStats((stats) => {
        setDeviceStats(stats);
        setLoading(false);
      });
    } catch (err: any) {
      console.error('Error loading dashboard stats:', err);
      setError(err.message || 'Failed to load dashboard statistics');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <PageHeader
          title={
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', lineHeight: '1.2' }}>Device Statistics Dashboard</div>
              <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginTop: '4px', lineHeight: '1.4' }}>
                Monitor device status based on data validity and connectivity
              </div>
            </div>
          }
        />
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Typography.Text>Loading dashboard statistics...</Typography.Text>
          </div>
        </div>
      </Space>
    );
  }

  if (error) {
    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <PageHeader
          title={
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', lineHeight: '1.2' }}>Device Statistics Dashboard</div>
              <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginTop: '4px', lineHeight: '1.4' }}>
                Monitor device status based on data validity and connectivity
              </div>
            </div>
          }
        />
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Typography.Text type="danger">{error}</Typography.Text>
                <br />
                <Typography.Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                  Please check your connection and try refreshing the page.
                </Typography.Text>
              </div>
            }
          />
        </Card>
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb>
            <Breadcrumb.Item>
              <span>DevStat Dashboard</span>
            </Breadcrumb.Item>
          </Breadcrumb>
        )}
        title="Device Statistics Dashboard"
        subTitle="Monitor device status based on data validity and connectivity"
      />
      
      {/* First row of widgets */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <TotalDevicesActive stats={deviceStats} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <DevicesOnlineInvalid stats={deviceStats} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <DevicesOffline stats={deviceStats} />
        </Col>
      </Row>
      
      {/* Second row of widgets */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <DeviceFailureRate stats={deviceStats} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <TotalDevicesDeployed stats={deviceStats} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <DevicesInactive stats={deviceStats} />
        </Col>
      </Row>
      
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <DeviceFailureList failureList={deviceStats.failureList} />
        </Col>
      </Row>
    </Space>
  );
}

export default DevStatDashboard;